import { inject, observer } from "mobx-react";
import { Fragment, useCallback, useMemo } from "react";
import type { BlockFragment, FeedResultFragment } from "@ihr-radioedit/inferno-webapi";
import { Grid, Heading } from "../../ui";
import { HeadingProps } from "../../ui/Heading.component";
import { AdPosition } from "../Ad.component";
import { CalendarDatasourceMenu } from "./CalendarDatasourceMenu.component";
import { DatasourceProps } from "./Datasource.component";
import "./Datasource.style.scss";
import { NoCalendarFeedItemMessage } from "./NoCalendarFeedItemMessage.component";
import { getEventStartDate } from "../../lib/calendar";
import { getLayout } from "../../lib/content";
import { removePrefix } from "@ihr-radioedit/inferno-core";
import loadable from "@loadable/component";
import { blockHaveAd, isFeedResult, isCalendarPublishRecord, showLoadMoreAd } from "@inferno/renderer-shared-core";

const ContentFeedItem = loadable(() => import("./ContentFeedItem.component"));

const CATEGORY_PREFIX_REGEX = /^[\w-]+\//;

const extractActiveEventCategories = (content: FeedResultFragment[] | null): Set<string> => {
  const activeCategories = new Set<string>();

  if (!Array.isArray(content)) {
    return activeCategories;
  }

  content.forEach(item => {
    if (!isFeedResult(item) || !isCalendarPublishRecord(item.record)) {
      return;
    }

    const subscription = item.record.subscription;
    if (!Array.isArray(subscription) || subscription.length === 0) {
      return;
    }

    const tags = subscription[0]?.tags;
    if (!Array.isArray(tags)) {
      return;
    }

    tags.forEach(tag => {
      if (typeof tag === "string" && tag) {
        const category = removePrefix(CATEGORY_PREFIX_REGEX)(tag);
        if (category && category !== tag) {
          activeCategories.add(category);
        }
      }
    });
  });

  return activeCategories;
};

export const CalendarDatasource = inject("store")(
  observer((props: DatasourceProps) => {
    const { store, title, content, feedId, displayHints, block } = props;

    if (!store) {
      return null;
    }

    const heading: HeadingProps = useMemo(() => props.heading ?? { level: 2 }, [props.heading]);
    const adBlockData = useMemo(() => ({ value: { ...block.value } } as BlockFragment), [block.value]);
    const { showEyebrow, enableLoadMore, showDateStamp } = displayHints;
    const adInsertPosition = useMemo(() => block.value.size || 10, [block.value.size]);
    const haveAds = useMemo(() => blockHaveAd(block.value.position), [block.value.position]);

    const header = useMemo(() => (title ? <Heading {...heading}>{title}</Heading> : null), [title, heading]);
    const showMenu = props.displayHints.hints?.includes("display-hints/calendar-show-menu");

    const items = useMemo(
      () =>
        content?.map((item, i) => {
          const { style } = displayHints;

          if (!isFeedResult(item) || !isCalendarPublishRecord(item.record)) {
            return null;
          }

          const showAd = showLoadMoreAd(content.length, adInsertPosition, i, enableLoadMore);
          return (
            <Fragment key={`feed-item-${feedId}-${i}`}>
              <ContentFeedItem
                item={item.record}
                index={i}
                showEyebrow={showEyebrow}
                showDateStamp={showDateStamp && !item.record.payload.fields?.hide_event_date?.value}
                datetime={getEventStartDate(item.record.payload.fields) ?? item.record.pub_start}
                layout={getLayout(style)}
              />
              {haveAds && showAd ? (
                <div className="load-more-ad">
                  <AdPosition block={adBlockData} batchId={`${feedId}-${Math.ceil(i / adInsertPosition)}`} />
                </div>
              ) : null}
            </Fragment>
          );
        }) ?? [],
      [
        adBlockData,
        adInsertPosition,
        content,
        displayHints,
        enableLoadMore,
        feedId,
        haveAds,
        showEyebrow,
        showDateStamp,
      ],
    );

    const isNoEvents = items.every(item => item === null);

    const getActiveEventCategories = useCallback(() => extractActiveEventCategories(content), [content]);

    const activeCategories = useMemo(() => {
      if (isNoEvents || !content?.length) {
        return undefined;
      }
      return getActiveEventCategories();
    }, [content, isNoEvents, getActiveEventCategories]);

    return (
      <Fragment key={block.id}>
        {header}
        {showMenu ? <CalendarDatasourceMenu store={store} activeCategories={activeCategories} /> : null}
        <div className="feed-cards" data-block-id={block.id} suppressHydrationWarning={true}>
          {!isNoEvents ? <Grid columns={displayHints.columns}>{items}</Grid> : <NoCalendarFeedItemMessage />}
        </div>
      </Fragment>
    );
  }),
);

export default CalendarDatasource;
